import React, { useState, useRef, useMemo } from 'react';
import * as XLSX from 'xlsx';
import DOMPurify from 'dompurify';
import { Download, Play, AlertTriangle, CheckCircle, FileSpreadsheet, X, RefreshCw,
         Package, Eye, ArrowLeft, Info, Clock, Upload, Globe, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { generateNorce, sleep } from './lib/prompt';
import { useData } from './contexts/DataContext';
import { VocabCollectionSelector } from './components/VocabCollectionSelector';
import { MarketFlag } from './lib/MarketFlag';

const STATUS = { idle: 'idle', preview: 'preview', running: 'running', done: 'done' };

// Norce Export required columns
const REQUIRED_COLS = ['PartNo', 'Name', 'Bullets', 'Market'];
const OPTIONAL_COLS = ['Category'];

// Market codes mapping
const MARKET_CODES = {
  'SE': { name: 'Sverige',       lang: 'Swedish' },
  'NO': { name: 'Norge',         lang: 'Norwegian' },
  'FI': { name: 'Finland',       lang: 'Finnish' },
  'DE': { name: 'Tyskland',      lang: 'German' },
  'DK': { name: 'Danmark',       lang: 'Danish' },
  'NL': { name: 'Nederländerna', lang: 'Dutch (Netherlands)' },
};

export default function BulkGenerator() {
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [parseError, setParseError] = useState('');
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState(STATUS.idle);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [currentProduct, setCurrentProduct] = useState('');
  const [selectedMarkets, setSelectedMarkets] = useState(['SE', 'NO', 'FI', 'DE', 'DK', 'NL']);
  const abortRef = useRef(false);
  const fileInputRef = useRef(null);
  const data = useData();
  const { categories, templates, vocabulary, vocabCollections, addToHistory, addLog, model, addUsageLog } = data;

  // Active vocabulary collections (null = all)
  const [activeCollections, setActiveCollections] = useState(null);
  const vocabForPrompt = activeCollections
    ? vocabulary.filter(e => activeCollections.includes(e.collection || 'Standard'))
    : vocabulary;
  const promptCtx = { brandGuidelines: templates.brandGuidelines, categoryTemplates: templates.categoryTemplates, vocabulary: vocabForPrompt, model };

  // Generation switches — session state
  const [genSwitches, setGenSwitches] = useState({ desc: true, metaTitle: true, metaDesc: true });
  const toggleGenSwitch = (key) => setGenSwitches(prev => ({ ...prev, [key]: !prev[key] }));

  // Norce publish state
  const [norceStatuses, setNorceStatuses] = useState({});
  const [norceErrors, setNorceErrors] = useState({});
  const [norceJobIds, setNorceJobIds] = useState({});
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishSummary, setPublishSummary] = useState(null);
  const [editingDesc, setEditingDesc] = useState(new Set());
  const [showNorceConfirm, setShowNorceConfirm] = useState(false);
  
  // Dynamic categories
  const defaultCategory = categories[0] || 'Stödstrumpor';

  // Validation warnings
  const warnings = useMemo(() => {
    if (rows.length === 0) return { missingBullets: [], duplicatePartNos: [], invalidCategories: [] };
    
    // Missing bullets
    const missingBullets = rows.filter(r => !r.bullets || r.bullets.trim().length === 0);
    
    // Duplicate PartNos
    const partNoCounts = {};
    rows.forEach(r => {
      partNoCounts[r.partNo] = (partNoCounts[r.partNo] || 0) + 1;
    });
    const duplicatePartNos = Object.entries(partNoCounts)
      .filter(([_, count]) => count > 1)
      .map(([partNo, count]) => ({ partNo, count }));
    
    // Invalid categories (no template)
    const { categoryTemplates } = templates;
    const invalidCategories = [...new Set(rows.map(r => r.category))]
      .filter(cat => !categoryTemplates[cat]?.trim());
    
    return { missingBullets, duplicatePartNos, invalidCategories };
  }, [rows, templates]);

  const hasWarnings = warnings.missingBullets.length > 0 || 
                      warnings.duplicatePartNos.length > 0 || 
                      warnings.invalidCategories.length > 0;

  // Parse and validate file with strict column checking
  const parseFile = (f) => {
    setParseError(''); setRows([]); setResults([]);
    const isCsv = f.name?.toLowerCase().endsWith('.csv');
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = isCsv
          ? XLSX.read(e.target.result, { type: 'string' })
          : XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
        const json = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' });
        
        if (!json.length) { 
          setParseError('Filen är tom.'); 
          return; 
        }
        
        const fileColumns = Object.keys(json[0]);
        const missing = REQUIRED_COLS.filter((c) => !fileColumns.includes(c));
        
        if (missing.length) {
          setParseError(
            `❌ Obligatoriska kolumner saknas: ${missing.join(', ')}\n\n` +
            `Filen måste innehålla dessa kolumner:\n` +
            `• PartNo — artikelnummer\n` +
            `• Name — produktnamn\n` +
            `• Bullets — råmaterial för AI (egenskaper, leverantörstext)\n` +
            `• Market — marknadskod (SE, NO, FI, DE, DK, NL)\n\n` +
            `Valfri kolumn: Category (om saknas används standardmall)`
          );
          return;
        }
        
        // Validate market codes
        const validMarkets = Object.keys(MARKET_CODES);
        const invalidRows = json.filter(r => r.Market && !validMarkets.includes(String(r.Market).toUpperCase().trim()));
        
        if (invalidRows.length > 0) {
          const badMarkets = [...new Set(invalidRows.map(r => r.Market))].join(', ');
          setParseError(
            `❌ Ogiltiga marknadskoder: ${badMarkets}\n\n` +
            `Tillåtna koder: SE, NO, FI, DE, DK, NL`
          );
          return;
        }
        
        const parsedRows = json
          .filter((r) => r.PartNo && r.Name && r.Market)
          .map((r) => ({
            partNo: String(r.PartNo).trim(),
            name: String(r.Name).trim(),
            bullets: String(r.Bullets || '').trim(),
            market: String(r.Market).toUpperCase().trim(),
            category: String(r.Category || defaultCategory).trim(),
          }));
        
        if (parsedRows.length === 0) {
          setParseError('Inga giltiga rader hittades. Kontrollera att PartNo, Name och Market är ifyllda.');
          return;
        }
        
        setRows(parsedRows);
        
        // Auto-select markets based on file content
        const marketsInFile = [...new Set(parsedRows.map(r => r.market))];
        setSelectedMarkets(marketsInFile);
        
        // Go to preview state
        setStatus(STATUS.preview);
        
        toast.success(`${parsedRows.length} produkter laddade — granska innan generering`);
        
      } catch (err) {
        console.error(err);
        setParseError('Kunde inte läsa filen. Kontrollera att det är en giltig .xlsx eller .csv.');
      }
    };
    if (isCsv) {
      reader.readAsText(f, 'UTF-8');
    } else {
      reader.readAsArrayBuffer(f);
    }
  };

  const handleFileInput = (e) => { 
    const f = e.target.files?.[0]; 
    if (f) { setFile(f); parseFile(f); } 
  };
  
  const handleDrop = (e) => { 
    e.preventDefault(); 
    const f = e.dataTransfer?.files?.[0]; 
    if (f) { setFile(f); parseFile(f); } 
  };

  const removeFile = () => {
    setFile(null); setRows([]); setResults([]); setParseError(''); setStatus(STATUS.idle);
    setNorceStatuses({}); setNorceErrors({}); setNorceJobIds({}); setIsPublishing(false); setPublishSummary(null); setShowNorceConfirm(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const toggleMarket = (code) => {
    setSelectedMarkets(prev =>
      prev.includes(code) ? prev.filter(m => m !== code) : [...prev, code]
    );
  };

  const marketsInFile = useMemo(() => {
    return Object.keys(MARKET_CODES).filter(code => rows.some(r => r.market === code));
  }, [rows]);

  const toggleAllMarkets = () => {
    const allAvailable = marketsInFile;
    setSelectedMarkets(prev =>
      prev.length === allAvailable.length ? [] : [...allAvailable]
    );
  };

  // Get rows filtered by selected markets
  const filteredRows = useMemo(() => {
    return rows.filter(r => selectedMarkets.includes(r.market));
  }, [rows, selectedMarkets]);

  // Group rows by market for preview
  const rowsByMarket = useMemo(() => {
    return selectedMarkets.reduce((acc, market) => {
      acc[market] = filteredRows.filter(r => r.market === market);
      return acc;
    }, {});
  }, [filteredRows, selectedMarkets]);

  const runGeneration = async (targetRows, onResult) => {
    abortRef.current = false;
    setStatus(STATUS.running);
    setProgress({ current: 0, total: targetRows.length });
    const out = [];
    
    for (let i = 0; i < targetRows.length; i++) {
      if (abortRef.current) break;
      const row = targetRows[i];
      setProgress({ current: i + 1, total: targetRows.length });
      setCurrentProduct(`${row.name} (${row.market})`);
      
      const { categoryTemplates } = templates;
      if (!categoryTemplates[row.category]?.trim()) {
        out.push({ 
          ...row, 
          description: '', 
          metaTitle: '', 
          metaDescription: '', 
          status: 'skipped',
          error: 'Kategori saknar mall'
        });
        onResult([...out]);
        continue;
      }
      
      try {
        const result = await generateNorce(row.name, row.category, row.bullets, row.market, promptCtx);
        const storedDesc = genSwitches.desc ? result.description : '';
        const wc = storedDesc.replace(/<[^>]+>/g, ' ').split(/\s+/).filter(Boolean).length;
        
        out.push({ 
          ...row, 
          description: storedDesc,
          metaTitle: genSwitches.metaTitle ? result.metaTitle : '',
          metaDescription: genSwitches.metaDesc ? result.metaDescription : '',
          status: 'ok', 
          needsReview: result.needsReview || false,
          wordCount: wc,
          metaTitleLength: genSwitches.metaTitle ? result.metaTitle.length : 0,
          metaDescriptionLength: genSwitches.metaDesc ? result.metaDescription.length : 0,
        });
        
        addToHistory({ 
          productName: row.name, 
          category: row.category, 
          market: row.market, 
          wordCount: wc, 
          source: 'bulk' 
        });
        if (result.usage) addUsageLog({ ...result.usage, market: row.market, category: row.category, source: 'bulk', module: 'bulk' });
        
      } catch (err) {
        out.push({ 
          ...row, 
          description: '', 
          metaTitle: '', 
          metaDescription: '', 
          status: 'error',
          error: err.message 
        });
      }
      
      onResult([...out]);
      if (i < targetRows.length - 1 && !abortRef.current) await sleep(2000);
    }
    return out;
  };

  const startGeneration = async () => {
    if (selectedMarkets.length === 0) {
      toast.error('Välj minst en marknad.');
      return;
    }
    if (filteredRows.length === 0) {
      toast.error('Inga produkter för valda marknader.');
      return;
    }
    
    setResults([]);
    const startTime = Date.now();
    const out = await runGeneration(filteredRows, setResults);
    setStatus(abortRef.current ? STATUS.preview : STATUS.done);
    
    if (!abortRef.current) {
      const okCount = out.filter((r) => r.status === 'ok').length;
      const failCount = out.filter((r) => r.status === 'error').length;
      const skipCount = out.filter((r) => r.status === 'skipped').length;
      const durationSeconds = Math.round((Date.now() - startTime) / 1000);
      const markets = [...new Set(out.map(r => r.market))];
      const logEntry = {
        type: 'norce',
        fileName: file?.name || 'Norce Export',
        markets,
        products: filteredRows.length,
        success: okCount,
        failed: failCount,
        skipped: skipCount,
        durationSeconds,
        perProduct: out.map(r => ({
          name: r.name, partNo: r.partNo, market: r.market,
          wordCount: r.wordCount, status: r.status, error: r.error,
        })),
      };
      addLog(logEntry);      toast.success(`Klar! ${okCount}/${filteredRows.length} genererade — ladda ner filerna nedan`);
    }
  };

  const retryFailed = async () => {
    const failedIdx = results.map((r, i) => (r.status === 'error' ? i : -1)).filter((i) => i !== -1);
    if (!failedIdx.length) return;
    
    const updated = [...results];
    abortRef.current = false;
    setStatus(STATUS.running);
    setProgress({ current: 0, total: failedIdx.length });
    
    for (let i = 0; i < failedIdx.length; i++) {
      if (abortRef.current) break;
      const idx = failedIdx[i];
      const row = updated[idx];
      setProgress({ current: i + 1, total: failedIdx.length });
      setCurrentProduct(`${row.name} (${row.market})`);
      
      try {
        const result = await generateNorce(row.name, row.category, row.bullets, row.market, promptCtx);
        const storedDesc = genSwitches.desc ? result.description : '';
        const wc = storedDesc.replace(/<[^>]+>/g, ' ').split(/\s+/).filter(Boolean).length;
        
        updated[idx] = { 
          ...row, 
          description: storedDesc,
          metaTitle: genSwitches.metaTitle ? result.metaTitle : '',
          metaDescription: genSwitches.metaDesc ? result.metaDescription : '',
          status: 'ok', 
          needsReview: result.needsReview || false,
          wordCount: wc,
          metaTitleLength: genSwitches.metaTitle ? result.metaTitle.length : 0,
          metaDescriptionLength: genSwitches.metaDesc ? result.metaDescription.length : 0,
        };
        
        addToHistory({ 
          productName: row.name, 
          category: row.category, 
          market: row.market, 
          wordCount: wc, 
          source: 'bulk' 
        });
        if (result.usage) addUsageLog({ ...result.usage, market: row.market, category: row.category, source: 'bulk', module: 'bulk' });
        
      } catch (err) {
        updated[idx] = { ...row, status: 'error', error: err.message };
      }
      
      setResults([...updated]);
      if (i < failedIdx.length - 1 && !abortRef.current) await sleep(2000);
    }
    
    const still = updated.filter((r) => r.status === 'error').length;
    setStatus(abortRef.current ? STATUS.preview : STATUS.done);
    if (!abortRef.current) {
      still === 0 ? toast.success('Alla rader klara!') : toast.warning(`${still} rad(er) misslyckades fortfarande.`);
    }
  };

  // Download per-market Excel files (Norce format)
  const downloadNorceExcel = (marketCode) => {
    const marketResults = results.filter(r => r.market === marketCode && r.status === 'ok');
    if (marketResults.length === 0) {
      toast.error(`Inga resultat för ${marketCode}`);
      return;
    }
    
    const data = marketResults.map(r => ({
      'PartNo': r.partNo,
      'Name': r.name,
      'Description': r.description,
      'Meta Title Override': r.metaTitle,
      'Meta Description Override': r.metaDescription,
    }));
    
    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = [
      { wch: 15 },  // PartNo
      { wch: 35 },  // Name
      { wch: 80 },  // Description
      { wch: 60 },  // Meta Title
      { wch: 80 },  // Meta Description
    ];
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Products');
    XLSX.writeFile(wb, `vardvaskan-${marketCode}.xlsx`);
    toast.success(`vardvaskan-${marketCode}.xlsx nedladdad!`);
  };

  // Download all market files
  const downloadAllNorce = () => {
    const marketsWithResults = [...new Set(results.filter(r => r.status === 'ok').map(r => r.market))];
    if (marketsWithResults.length === 0) {
      toast.error('Inga resultat att ladda ner');
      return;
    }
    
    marketsWithResults.forEach((market, i) => {
      setTimeout(() => downloadNorceExcel(market), i * 500);
    });
  };

  const downloadTemplate = () => {
    const data = [
      { 
        PartNo: '10001', 
        Name: 'Stödstrumpa Bambu Pro', 
        Bullets: 'Kompressionsklass I (16-18 mmHg)\nHögt bomullsinnehåll 80%\nAnatomisk fotdel\nMjuk resår utan åtstramning\nVentilerande material', 
        Market: 'SE',
        Category: 'Stödstrumpor'
      },
      { 
        PartNo: '10002', 
        Name: 'Støttestrømpe Komfort Plus', 
        Bullets: 'Kompresjonsklasse I\nBambusmateriale\nAnatomisk fotform\nPustende stoff', 
        Market: 'NO', 
        Category: 'Stödstrumpor' 
      },
      { 
        PartNo: '10003', 
        Name: 'Tukisukka Premium', 
        Bullets: 'Kompressioaste I\nBambumateriaali\nAnatominen jalkateräosa\nHengittävä kangas', 
        Market: 'FI', 
        Category: 'Stödstrumpor' 
      },
      { 
        PartNo: '10004', 
        Name: 'Arbetsbyxa Stretch Pro', 
        Bullets: 'Fyrvägsstretch\nSlitstarkt tyg 280g/m²\nKnäskyddsfickor\nMultiple verktysfickor\nRip-stop material', 
        Market: 'SE', 
        Category: 'Arbetskläder' 
      },
    ];
    
    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = [
      { wch: 12 },  // PartNo
      { wch: 30 },  // Name
      { wch: 55 },  // Bullets
      { wch: 8 },   // Market
      { wch: 15 },  // Category
    ];
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Produkter');
    XLSX.writeFile(wb, 'vardvaskan-norce-mall.xlsx');
    toast.success('Norce-mall nedladdad!');
  };

  const publishToNorce = async () => {
    const publishable = results.filter(r => r.status === 'ok');
    if (publishable.length === 0) return;

    // Mark all as publishing
    const initialStatuses = {};
    publishable.forEach(r => { initialStatuses[`${r.partNo}-${r.market}`] = 'publishing'; });
    setNorceStatuses(initialStatuses);
    setNorceErrors({});
    setIsPublishing(true);
    setPublishSummary(null);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${API_URL}/api/norce/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          products: publishable.map(r => ({
            partNo: r.partNo,
            market: r.market,
            description: r.description || '',
            metaTitle: r.metaTitle || '',
            metaDescription: r.metaDescription || '',
          })),
        }),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${resp.status}`);
      }

      const { results: norceResults } = await resp.json();
      const newStatuses = {};
      const newErrors = {};
      const newJobIds = {};
      let successCount = 0;

      norceResults.forEach(nr => {
        const key = `${nr.partNo}-${nr.market}`;
        newStatuses[key] = nr.success ? 'success' : 'error';
        if (!nr.success) newErrors[key] = nr.error || 'Okänt fel';
        if (nr.job_id) newJobIds[key] = nr.job_id;
        if (nr.success) successCount++;
      });

      setNorceStatuses(newStatuses);
      setNorceErrors(newErrors);
      setNorceJobIds(newJobIds);
      setPublishSummary({ success: successCount, total: norceResults.length });

      if (successCount === norceResults.length) {
        toast.success(`${successCount} av ${norceResults.length} produkter publicerade till Norce!`);
      } else {
        toast.warning(`${successCount} av ${norceResults.length} publicerade — ${norceResults.length - successCount} misslyckades.`);
      }
    } catch (err) {
      toast.error(`Norce-publicering misslyckades: ${err.message}`);
      setNorceStatuses({});
    } finally {
      setIsPublishing(false);
    }
  };

  const republishProduct = async (row) => {
    const key = `${row.partNo}-${row.market}`;
    setNorceStatuses(prev => ({ ...prev, [key]: 'publishing' }));
    setNorceErrors(prev => { const n = { ...prev }; delete n[key]; return n; });

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${API_URL}/api/norce/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          products: [{
            partNo: row.partNo,
            market: row.market,
            description: row.description || '',
            metaTitle: row.metaTitle || '',
            metaDescription: row.metaDescription || '',
          }],
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${resp.status}`);
      }
      const { results: norceResults } = await resp.json();
      const nr = norceResults[0];
      if (nr.success) {
        setNorceStatuses(prev => ({ ...prev, [key]: 'success' }));
        if (nr.job_id) setNorceJobIds(prev => ({ ...prev, [key]: nr.job_id }));
        setPublishSummary(prev => prev ? { ...prev, success: prev.success + 1 } : null);
        toast.success(`${row.name} publicerad till Norce!`);
      } else {
        setNorceStatuses(prev => ({ ...prev, [key]: 'error' }));
        setNorceErrors(prev => ({ ...prev, [key]: nr.error || 'Okänt fel' }));
        toast.error(`Misslyckades: ${nr.error}`);
      }
    } catch (err) {
      setNorceStatuses(prev => ({ ...prev, [key]: 'error' }));
      setNorceErrors(prev => ({ ...prev, [key]: err.message }));
      toast.error(`Re-publicering misslyckades: ${err.message}`);
    }
  };

  const isRunning = status === STATUS.running;
  const isDone = status === STATUS.done;
  const isPreview = status === STATUS.preview;
  const hasResults = results.length > 0;
  const failedCount = results.filter((r) => r.status === 'error').length;
  const pct = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;
  
  // Markets with results for download
  const marketsWithResults = [...new Set(results.filter(r => r.status === 'ok').map(r => r.market))];
  const marketCounts = marketsWithResults.reduce((acc, m) => {
    acc[m] = results.filter(r => r.market === m && r.status === 'ok').length;
    return acc;
  }, {});

  return (
    <div className="bulk-root">
      {/* Header */}
      <div className="norce-header">
        <div className="norce-header-left">
          <Package size={20} className="norce-icon" />
          <div>
            <h2 className="norce-title">Norce Export</h2>
            <p className="norce-subtitle">Generera produktbeskrivningar och SEO-data för import till Norce</p>
          </div>
        </div>
      </div>

      {/* Drop zone - only show when idle */}
      {status === STATUS.idle && !file && (
        <div 
          data-testid="drop-zone" 
          className="drop-zone" 
          onDrop={handleDrop} 
          onDragOver={(e) => e.preventDefault()} 
          onClick={() => fileInputRef.current?.click()}
        >
          <FileSpreadsheet size={32} className="drop-icon" />
          <p className="drop-title">Dra och släpp din Excel- eller CSV-fil här</p>
          <p className="drop-sub">eller klicka för att bläddra</p>
          <span className="drop-badge">.xlsx &nbsp;·&nbsp; .csv</span>
          <input 
            ref={fileInputRef} 
            type="file" 
            accept=".xlsx,.csv,.xls" 
            onChange={handleFileInput} 
            className="sr-only" 
            data-testid="file-input" 
          />
        </div>
      )}

      {/* Column requirements - only show when idle */}
      {status === STATUS.idle && !file && (
        <div className="norce-cols-info">
          <div className="norce-cols-section">
            <span className="norce-cols-label">Obligatoriska kolumner:</span>
            <div className="norce-cols-list">
              <code className="col-chip col-chip--required">PartNo</code>
              <code className="col-chip col-chip--required">Name</code>
              <code className="col-chip col-chip--required">Bullets</code>
              <code className="col-chip col-chip--required">Market</code>
            </div>
          </div>
          <div className="norce-cols-section">
            <span className="norce-cols-label">Valfri kolumn:</span>
            <div className="norce-cols-list">
              <code className="col-chip col-chip--optional">Category</code>
            </div>
          </div>
          <div className="norce-cols-section">
            <span className="norce-cols-label">Marknadskoder:</span>
            <div className="norce-market-codes">
              {Object.entries(MARKET_CODES).map(([code, info]) => (
                <span key={code} className="market-code-chip">
                  <MarketFlag market={code} size="1em" /> {code}
                </span>
              ))}
            </div>
          </div>
          <button 
            data-testid="download-template-btn" 
            className="norce-template-btn" 
            onClick={(e) => { e.stopPropagation(); downloadTemplate(); }}
          >
            <Download size={14} />
            Ladda ner Norce-mall
          </button>
        </div>
      )}

      {/* Parse error */}
      {parseError && (
        <div data-testid="parse-error" className="alert alert--error alert--block">
          <AlertTriangle size={14} />
          <pre className="parse-error-text">{parseError}</pre>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          PREVIEW & VALIDATE STEP
         ═══════════════════════════════════════════════════════════ */}
      {isPreview && (
        <div className="preview-container">
          {/* File info bar */}
          <div className="file-bar file-bar--preview">
            <div className="file-bar-left">
              <FileSpreadsheet size={16} className="file-icon" />
              <span className="file-name">{file?.name}</span>
              <span className="file-count">{rows.length} produkter</span>
            </div>
            <button className="file-remove" onClick={removeFile}><X size={14} /></button>
          </div>

          {/* Preview header */}
          <div className="preview-header">
            <div className="preview-header-left">
              <Eye size={18} className="preview-icon" />
              <div>
                <h3 className="preview-title">Granska &amp; Validera</h3>
                <p className="preview-subtitle">Verifiera produkterna innan du startar genereringen</p>
              </div>
            </div>
            <ChevronRight size={18} className="preview-header-chevron" />
          </div>

          {/* Summary stats */}
          <div className="preview-stats">
            <div className="preview-stat">
              <Package size={16} className="preview-stat-icon" />
              <div className="preview-stat-body">
                <span className="preview-stat-value">{filteredRows.length}</span>
                <span className="preview-stat-label">produkter</span>
              </div>
            </div>
            <div className="preview-stat">
              <Globe size={16} className="preview-stat-icon" />
              <div className="preview-stat-body">
                <span className="preview-stat-value">{selectedMarkets.length}</span>
                <span className="preview-stat-label">marknader</span>
              </div>
            </div>
            <div className="preview-stat">
              <FileSpreadsheet size={16} className="preview-stat-icon" />
              <div className="preview-stat-body">
                <span className="preview-stat-value">{selectedMarkets.length}</span>
                <span className="preview-stat-label">Excel-filer</span>
              </div>
            </div>
          </div>

          {/* Market selection */}
          <div className="preview-markets">
            <div className="preview-section-header">
              <span className="preview-section-label">Marknader att generera</span>
              <button
                type="button"
                className="bulk-select-all-btn"
                onClick={toggleAllMarkets}
                disabled={marketsInFile.length === 0}
              >
                {selectedMarkets.length === marketsInFile.length && marketsInFile.length > 0
                  ? 'Avmarkera alla' : 'Välj alla'}
              </button>
            </div>
            <div className="bulk-market-list">
              {Object.entries(MARKET_CODES).map(([code, info]) => {
                const countInFile = rows.filter(r => r.market === code).length;
                const isAvailable = countInFile > 0;
                const isSelected  = selectedMarkets.includes(code);

                return (
                  <label
                    key={code}
                    data-testid={`bulk-market-${code}`}
                    className={`bulk-market-row${isSelected ? ' bulk-market-row--on' : ''}${!isAvailable ? ' bulk-market-row--off' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={!isAvailable}
                      onChange={() => toggleMarket(code)}
                      className="bulk-market-checkbox"
                    />
                    <MarketFlag market={code} size="1.15em" />
                    <span className="bulk-market-name">{info.name}</span>
                    {isAvailable && (
                      <span className="bulk-market-badge">
                        {countInFile} produkt{countInFile !== 1 ? 'er' : ''}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>

          {/* Generation switches */}
          <div className="preview-gen-switches" data-testid="bulk-gen-switches">
            <span className="preview-section-label">Fält att generera:</span>
            <div className="bulk-switch-row">
              {[
                { key: 'desc', label: 'Produktbeskrivning' },
                { key: 'metaTitle', label: 'Meta Title' },
                { key: 'metaDesc', label: 'Meta Description' },
              ].map(({ key, label }) => (
                <label key={key} className="gen-switch-row gen-switch-row--inline" data-testid={`bulk-switch-${key}`}>
                  <button
                    type="button"
                    className={`toggle-switch${genSwitches[key] ? ' toggle-switch--on' : ''}`}
                    onClick={() => toggleGenSwitch(key)}
                    aria-checked={genSwitches[key]}
                    role="switch"
                  >
                    <span className="toggle-knob" />
                  </button>
                  <span className="gen-switch-label">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Vocabulary collection selector */}
          <VocabCollectionSelector
            vocabCollections={vocabCollections}
            activeCollections={activeCollections}
            onChange={setActiveCollections}
          />

          {/* Warnings */}
          {hasWarnings && (
            <div className="preview-warnings">
              <div className="preview-warning-header">
                <AlertTriangle size={16} />
                <span>Varningar att granska</span>
              </div>
              
              {warnings.missingBullets.length > 0 && (
                <div className="preview-warning-item">
                  <span className="preview-warning-badge preview-warning-badge--yellow">
                    {warnings.missingBullets.length}
                  </span>
                  <span className="preview-warning-text">
                    produkt{warnings.missingBullets.length !== 1 ? 'er' : ''} saknar Bullets (AI genererar baserat på produktnamn)
                  </span>
                </div>
              )}
              
              {warnings.duplicatePartNos.length > 0 && (
                <div className="preview-warning-item">
                  <span className="preview-warning-badge preview-warning-badge--orange">
                    {warnings.duplicatePartNos.length}
                  </span>
                  <span className="preview-warning-text">
                    duplicerade PartNo: {warnings.duplicatePartNos.map(d => `${d.partNo} (${d.count}×)`).join(', ')}
                  </span>
                </div>
              )}
              
              {warnings.invalidCategories.length > 0 && (
                <div className="preview-warning-item">
                  <span className="preview-warning-badge preview-warning-badge--red">
                    {warnings.invalidCategories.length}
                  </span>
                  <span className="preview-warning-text">
                    kategori{warnings.invalidCategories.length !== 1 ? 'er' : ''} utan mall: {warnings.invalidCategories.join(', ')} 
                    <em> — dessa produkter hoppas över</em>
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Product list by market */}
          <div className="preview-products">
            <span className="preview-section-label">Produkter per marknad:</span>
            
            {selectedMarkets.map(market => {
              const marketRows = rowsByMarket[market] || [];
              if (marketRows.length === 0) return null;
              
              return (
                <div key={market} className="preview-market-section">
                  <div className="preview-market-header">
                    <MarketFlag market={market} />
                    <span className="preview-market-name">{MARKET_CODES[market]?.name}</span>
                    <span className="preview-market-count">{marketRows.length} produkt{marketRows.length !== 1 ? 'er' : ''}</span>
                    <span className="preview-market-file">→ vardvaskan-{market}.xlsx</span>
                  </div>
                  
                  <div className="preview-product-table-wrap">
                    <table className="preview-product-table">
                      <thead>
                        <tr>
                          <th className="col-partno">PartNo</th>
                          <th className="col-name">Name</th>
                          <th className="col-category">Category</th>
                          <th className="col-bullets">Bullets</th>
                          <th className="col-status">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {marketRows.map((row, i) => {
                          const hasBullets = row.bullets && row.bullets.trim().length > 0;
                          const { categoryTemplates } = templates;
                          const hasTemplate = categoryTemplates[row.category]?.trim();
                          
                          return (
                            <tr key={i} className={!hasTemplate ? 'row-warning' : ''}>
                              <td className="col-partno"><code>{row.partNo}</code></td>
                              <td className="col-name">{row.name}</td>
                              <td className="col-category">{row.category}</td>
                              <td className="col-bullets">
                                {hasBullets ? (
                                  <span className="bullets-preview">{row.bullets.substring(0, 50)}{row.bullets.length > 50 ? '...' : ''}</span>
                                ) : (
                                  <span className="bullets-missing">Saknas</span>
                                )}
                              </td>
                              <td className="col-status">
                                {!hasTemplate ? (
                                  <span className="status-badge status-badge--skip">Hoppas över</span>
                                ) : !hasBullets ? (
                                  <span className="status-badge status-badge--warn">Varning</span>
                                ) : (
                                  <span className="status-badge status-badge--ok">OK</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Output info */}
          <div className="preview-output-info">
            <Info size={16} />
            <div>
              <strong>Vad genereras per produkt:</strong>
              <ul>
                <li>Description — produktbeskrivning (200-400 ord)</li>
                <li>Meta Title Override — SEO-titel (50-55 tecken)</li>
                <li>Meta Description Override — SEO-beskrivning (150-160 tecken)</li>
              </ul>
            </div>
          </div>

          {/* Estimated generation time */}
          {filteredRows.length > 0 && selectedMarkets.length > 0 && (() => {
            const totalSeconds = filteredRows.length * 15 * 5;
            const minutes = totalSeconds / 60;
            let label, warning;
            if (minutes < 1) {
              label = 'Less than 1 minute';
              warning = false;
            } else if (minutes <= 5) {
              label = `~${Math.round(minutes)} minute${Math.round(minutes) === 1 ? '' : 's'}`;
              warning = false;
            } else {
              label = `~${Math.round(minutes)} minutes — consider splitting into smaller batches`;
              warning = true;
            }
            return (
              <div className={`time-estimate${warning ? ' time-estimate--warn' : ''}`} data-testid="time-estimate">
                <Clock size={13} />
                <span>Estimated generation time: <strong>{label}</strong></span>
              </div>
            );
          })()}

          {/* Action buttons */}
          <div className="preview-actions">
            <button className="preview-btn-cancel" onClick={removeFile}>
              <ArrowLeft size={14} />
              Avbryt
            </button>
            <button 
              className="preview-btn-start"
              onClick={startGeneration}
              disabled={filteredRows.length === 0 || selectedMarkets.length === 0}
              data-testid="start-generation-btn"
            >
              <Play size={14} />
              Starta generering ({filteredRows.length} produkter)
            </button>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          RUNNING / DONE STATE
         ═══════════════════════════════════════════════════════════ */}
      
      {/* File bar during running/done */}
      {(isRunning || isDone) && file && (
        <div className="file-bar">
          <div className="file-bar-left">
            <FileSpreadsheet size={16} className="file-icon" />
            <span className="file-name">{file.name}</span>
            <span className="file-count">{filteredRows.length} produkter</span>
          </div>
          {!isRunning && <button className="file-remove" onClick={removeFile}><X size={14} /></button>}
        </div>
      )}

      {/* Actions during running/done */}
      {(isRunning || isDone) && (
        <div className="bulk-actions">
          {isRunning ? (
            <button 
              data-testid="stop-generation-btn" 
              className="stop-btn" 
              onClick={() => { abortRef.current = true; setStatus(STATUS.preview); }}
            >
              <X size={15} />Avbryt
            </button>
          ) : null}
          
          {failedCount > 0 && isDone && (
            <button data-testid="retry-failed-btn" className="retry-btn" onClick={retryFailed}>
              <RefreshCw size={14} />Försök igen ({failedCount} misslyckade)
            </button>
          )}
        </div>
      )}

      {/* Progress */}
      {isRunning && (
        <div data-testid="progress-indicator" className="progress-wrap">
          <div className="progress-header">
            <span className="progress-label">Genererar {progress.current}/{progress.total} — <em>{currentProduct}</em></span>
            <span className="progress-pct">{pct}%</span>
          </div>
          <div className="progress-track"><div className="progress-bar" style={{ width: `${pct}%` }} /></div>
        </div>
      )}

      {/* Download section */}
      {isDone && marketsWithResults.length > 0 && (
        <div className="norce-download-section">
          <div className="norce-download-header">
            <CheckCircle size={18} className="norce-download-icon" />
            <div>
              <h3 className="norce-download-title">Filer redo för nedladdning</h3>
              <p className="norce-download-subtitle">
                {marketsWithResults.length} fil{marketsWithResults.length !== 1 ? 'er' : ''} genererade i Norce-format
              </p>
            </div>
          </div>
          
          <div className="norce-download-grid">
            {marketsWithResults.map(market => (
              <button
                key={market}
                className="norce-download-card"
                onClick={() => downloadNorceExcel(market)}
                data-testid={`download-${market}`}
              >
                <span className="norce-download-flag"><MarketFlag market={market} /></span>
                <div className="norce-download-info">
                  <span className="norce-download-filename">vardvaskan-{market}.xlsx</span>
                  <span className="norce-download-count">{marketCounts[market]} produkt{marketCounts[market] !== 1 ? 'er' : ''}</span>
                </div>
                <Download size={16} className="norce-download-icon-dl" />
              </button>
            ))}
          </div>
          
          {marketsWithResults.length > 1 && (
            <button className="norce-download-all" onClick={downloadAllNorce}>
              <Download size={14} />
              Ladda ner alla ({marketsWithResults.length} filer)
            </button>
          )}
        </div>
      )}

      {/* Norce Publish section */}
      {isDone && results.filter(r => r.status === 'ok').length > 0 && (
        <div className="norce-publish-section" data-testid="norce-publish-section">
          <div className="norce-publish-header">
            <Upload size={18} className="norce-publish-icon" />
            <div>
              <h3 className="norce-publish-title">Publicera till Norce</h3>
              <p className="norce-publish-subtitle">Skicka genererade texter direkt till Norce Commerce via API — ingen Excel-import behövs</p>
            </div>
          </div>

          {publishSummary && (
            <div className={`norce-publish-summary ${publishSummary.success === publishSummary.total ? 'norce-publish-summary--success' : 'norce-publish-summary--partial'}`} data-testid="norce-publish-summary">
              {publishSummary.success === publishSummary.total
                ? <CheckCircle size={15} />
                : <AlertTriangle size={15} />}
              <span>{publishSummary.success} av {publishSummary.total} produkter publicerade till Norce</span>
            </div>
          )}

          <button
            className={`norce-publish-btn${isPublishing ? ' norce-publish-btn--loading' : ''}`}
            onClick={() => setShowNorceConfirm(true)}
            disabled={isPublishing}
            data-testid="publish-to-norce-btn"
          >
            {isPublishing ? (
              <><div className="spinner norce-publish-spinner" />Publicerar...</>
            ) : (
              <><Upload size={15} />Publicera till Norce ({results.filter(r => r.status === 'ok').length} produkter)</>
            )}
          </button>
        </div>
      )}

      {/* Results table */}
      {hasResults && (
        <div data-testid="results-table" className="results-wrap">
          <div className="results-header">
            <span className="results-title">
              Resultat — <span className="results-count">{results.filter((r) => r.status === 'ok').length}/{results.length} klara</span>
            </span>
            {isDone && <div className="results-done-badge"><CheckCircle size={13} /><span>Klar</span></div>}
          </div>
          
          <div className="results-table-scroll">
            <table className="results-table norce-results-table">
              <thead>
                <tr>
                  <th className="col-num">#</th>
                  <th className="col-partno">PartNo</th>
                  <th className="col-product">Name</th>
                  <th className="col-market">Marknad</th>
                  <th className="col-desc">Description</th>
                  <th className="col-meta">Meta Title</th>
                  <th className="col-meta">Meta Desc</th>
                  <th className="col-status">Status</th>
                  <th className="col-norce">Norce</th>
                  <th className="col-jobid">Job ID</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r, i) => {
                  const wc = r.wordCount || 0;
                  const inRange = wc >= 200 && wc <= 400;
                  const titleOk = r.metaTitleLength >= 50 && r.metaTitleLength <= 55;
                  const descOk = r.metaDescriptionLength >= 145 && r.metaDescriptionLength <= 160;
                  
                  return (
                    <tr key={`${r.partNo || i}-${r.market || i}`} className={r.status === 'error' ? 'row-error' : r.status === 'skipped' ? 'row-skipped' : ''}>
                      <td className="col-num">{i + 1}</td>
                      <td className="col-partno"><code>{r.partNo}</code></td>
                      <td className="col-product"><span className="product-name">{r.name}</span></td>
                      <td className="col-market">
                        <span className="market-badge">
                          <MarketFlag market={r.market} size="1em" /> {MARKET_CODES[r.market]?.name || r.market}
                        </span>
                      </td>
                      <td className="col-desc">
                        {r.status === 'ok' ? (
                          <div className="result-cell-desc">
                            {editingDesc.has(i) ? (
                              <textarea
                                className="result-textarea"
                                value={r.description}
                                rows={5}
                                onChange={(e) => {
                                  const u = [...results];
                                  const wc = e.target.value.replace(/<[^>]+>/g, ' ').split(/\s+/).filter(Boolean).length;
                                  u[i] = { ...u[i], description: e.target.value, wordCount: wc };
                                  setResults(u);
                                }}
                              />
                            ) : (
                              <div
                                className="result-desc-preview"
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(r.description || '<em>—</em>') }}
                              />
                            )}
                            <div className="desc-cell-footer">
                              <span className={`wc-chip ${inRange ? 'wc-ok' : 'wc-warn'}`}>{wc} ord</span>
                              <button
                                className="desc-edit-toggle"
                                onClick={() => {
                                  const next = new Set(editingDesc);
                                  next.has(i) ? next.delete(i) : next.add(i);
                                  setEditingDesc(next);
                                }}
                              >
                                {editingDesc.has(i) ? 'Förhandsgranska' : 'Redigera'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <span className="result-error">{r.error || 'Fel'}</span>
                        )}
                      </td>
                      <td className="col-meta">
                        {r.status === 'ok' ? (
                          <div className="result-cell-meta">
                            <input 
                              className="result-input" 
                              value={r.metaTitle}
                              onChange={(e) => { 
                                const u = [...results]; 
                                u[i] = { ...u[i], metaTitle: e.target.value, metaTitleLength: e.target.value.length }; 
                                setResults(u); 
                              }} 
                            />
                            <span className={`char-chip ${titleOk ? 'char-ok' : 'char-warn'}`}>{r.metaTitleLength} tkn</span>
                          </div>
                        ) : '—'}
                      </td>
                      <td className="col-meta">
                        {r.status === 'ok' ? (
                          <div className="result-cell-meta">
                            <input 
                              className="result-input" 
                              value={r.metaDescription}
                              onChange={(e) => { 
                                const u = [...results]; 
                                u[i] = { ...u[i], metaDescription: e.target.value, metaDescriptionLength: e.target.value.length }; 
                                setResults(u); 
                              }} 
                            />
                            <span className={`char-chip ${descOk ? 'char-ok' : 'char-warn'}`}>{r.metaDescriptionLength} tkn</span>
                          </div>
                        ) : '—'}
                      </td>
                      <td className="col-status">
                        {r.status === 'ok' ? (
                          r.needsReview ? (
                            <span className="status-needs-review" title="Manuell kontroll krävs — AI nådde inte exakta teckengränser efter max försök">
                              <AlertTriangle size={14} />
                              <span>Kontrollera</span>
                            </span>
                          ) : (
                            <CheckCircle size={16} className="status-ok" />
                          )
                        ) : (
                          <AlertTriangle size={16} className="status-err" />
                        )}
                      </td>
                      <td className="col-norce">
                        {(() => {
                          const key = `${r.partNo}-${r.market}`;
                          const ns = norceStatuses[key];
                          if (!ns) return <span className="norce-cell-idle">—</span>;
                          if (ns === 'publishing') return <div className="spinner norce-cell-spinner" />;
                          if (ns === 'success') return <span className="norce-cell-ok"><CheckCircle size={13} />Publicerad</span>;
                          return (
                            <div className="norce-cell-err-wrap">
                              <span className="norce-cell-err" title={norceErrors[key] || 'Fel'}>
                                <AlertTriangle size={13} />Fel
                              </span>
                              {r.status === 'ok' && (
                                <button
                                  className="norce-republish-btn"
                                  onClick={() => republishProduct(r)}
                                  title={`Re-publicera ${r.name}`}
                                  data-testid={`republish-${r.partNo}-${r.market}`}
                                >
                                  <RefreshCw size={11} />
                                </button>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="col-jobid">
                        {(() => {
                          const key = `${r.partNo}-${r.market}`;
                          const jid = norceJobIds[key];
                          if (!jid) return <span className="jobid-empty">—</span>;
                          return (
                            <button
                              className="jobid-chip"
                              title={`Job ID: ${jid} — klicka för att kopiera`}
                              data-testid={`jobid-${r.partNo}-${r.market}`}
                              onClick={() => {
                                navigator.clipboard.writeText(String(jid));
                                toast.success(`Job ID ${jid} kopierat!`);
                              }}
                            >
                              #{jid}
                            </button>
                          );
                        })()}
                      </td>
                    </tr>
                  );
                })}
                {isRunning && (
                  <tr className="row-loading">
                    <td className="col-num">{progress.current + 1}</td>
                    <td colSpan={9}>
                      <div className="loading-row-inner">
                        <div className="spinner spinner--dark" />
                        <span>Genererar {currentProduct}...</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* Norce Confirmation Modal */}
      {showNorceConfirm && (() => {
        const publishable = results.filter(r => r.status === 'ok');
        const markets = [...new Set(publishable.map(r => r.market))];
        return (
          <div className="norce-confirm-overlay" data-testid="norce-confirm-overlay" onClick={() => setShowNorceConfirm(false)}>
            <div className="norce-confirm-modal" data-testid="norce-confirm-modal" onClick={e => e.stopPropagation()}>
              <h3 className="norce-confirm-title">Bekräfta publicering</h3>

              <div className="norce-confirm-stats">
                <div className="norce-confirm-stat-row">
                  <span className="norce-confirm-stat-label">Produkter</span>
                  <span className="norce-confirm-stat-value">{publishable.length} st</span>
                </div>
                <div className="norce-confirm-stat-row">
                  <span className="norce-confirm-stat-label">Marknader</span>
                  <div className="norce-confirm-markets">
                    {markets.map(m => (
                      <span key={m} className="norce-confirm-market-badge">
                        <MarketFlag market={m} size="1em" /> {MARKET_CODES[m]?.name || m}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="norce-confirm-warning">
                <AlertTriangle size={15} className="norce-confirm-warning-icon" />
                <p>
                  <strong>OBS:</strong> Detta skriver över befintliga produktbeskrivningar och meta-fält i Norce för alla {publishable.length} produkter. Åtgärden kan inte ångras.
                </p>
              </div>

              <div className="norce-confirm-actions">
                <button
                  className="norce-confirm-cancel"
                  onClick={() => setShowNorceConfirm(false)}
                  data-testid="norce-confirm-cancel"
                >
                  Avbryt
                </button>
                <button
                  className="norce-confirm-ok"
                  onClick={() => { setShowNorceConfirm(false); publishToNorce(); }}
                  data-testid="norce-confirm-ok"
                >
                  <Upload size={14} />
                  Ja, publicera till Norce
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
